/**
 * A server function that fails to start answers 503 with code BOOT_ERROR
 * (a cold-start failure on the platform). The request never reached our code,
 * so retrying it is safe, also for writes. Retry such answers a few times
 * with a short pause; every other result is returned unchanged.
 */
type InvokeResult = { data: unknown; error: unknown };
type Invoke = (name: string, options?: unknown) => Promise<InvokeResult>;

export async function isBootError(error: unknown): Promise<boolean> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (!(ctx instanceof Response) || ctx.status !== 503) return false;
  try {
    const body = await ctx.clone().json();
    return (body as { code?: string } | null)?.code === "BOOT_ERROR";
  } catch {
    return false;
  }
}

export function withBootRetry(invoke: Invoke, retries = 2, delayMs = 600): Invoke {
  return async (name, options) => {
    let result = await invoke(name, options);
    for (let i = 0; i < retries && result.error && (await isBootError(result.error)); i++) {
      await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      result = await invoke(name, options);
    }
    return result;
  };
}

let installed = false;
export function installFunctionsBootRetry(functions: { invoke: Invoke }) {
  if (installed) return;
  installed = true;
  functions.invoke = withBootRetry(functions.invoke.bind(functions));
}
